import React, { useState } from 'react';
import { User, MapPin, Edit } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useQueryClient } from '@tanstack/react-query';
import AddEntityModal from '../Entities/AddEntityModal';
import ErrorMessage from '../ErrorMessage';
import EntityDocuments from './EntityDocuments';

const EntityIdentity: React.FC = () => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';

  // Fetch entity details from API
  const entityQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}',
    {
      params: {
        path: {
          organization_id: orgId,
          id: entityId,
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  const entity = entityQuery.data;
  const isCompany = entity?.kind === 'company';

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditSubmit = () => {
    setIsEditModalOpen(false);
    // Invalidate and refetch entity data
    queryClient.invalidateQueries({
      queryKey: ['get', '/api/organization/{organization_id}/entities/{id}'],
    });
    entityQuery.refetch();
  };

  if (entityQuery.isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Chargement des informations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (entityQuery.error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <ErrorMessage error={entityQuery.error} />
        </div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Entité introuvable</h3>
            <p className="text-gray-500">L'entité demandée n'existe pas</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Informations Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Informations</span>
          </h3>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
            <span>Modifier</span>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {isCompany ? (
            <>
              <div>
                <span className="text-gray-600">Nom:</span>
                <span className="ml-2 font-medium">{entity.name}</span>
              </div>
              {entity.enterprise_number && (
                <div>
                  <span className="text-gray-600">Numéro BCE:</span>
                  <span className="ml-2 font-medium">{entity.enterprise_number}</span>
                </div>
              )}
              {entity.email && (
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{entity.email}</span>
                </div>
              )}
              {entity.phone && (
                <div>
                  <span className="text-gray-600">Téléphone:</span>
                  <span className="ml-2 font-medium">{entity.phone}</span>
                </div>
              )}
              {entity.country && (
                <div>
                  <span className="text-gray-600">Pays:</span>
                  <span className="ml-2 font-medium">{entity.country}</span>
                </div>
              )}
            </>
          ) : (
            <>
              {entity.first_name && (
                <div>
                  <span className="text-gray-600">Prénom:</span>
                  <span className="ml-2 font-medium">{entity.first_name}</span>
                </div>
              )}
              {entity.last_name && (
                <div>
                  <span className="text-gray-600">Nom:</span>
                  <span className="ml-2 font-medium">{entity.last_name}</span>
                </div>
              )}
              {entity.name && (
                <div>
                  <span className="text-gray-600">Nom complet:</span>
                  <span className="ml-2 font-medium">{entity.name}</span>
                </div>
              )}
              {entity.email && (
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{entity.email}</span>
                </div>
              )}
              {entity.phone && (
                <div>
                  <span className="text-gray-600">Téléphone:</span>
                  <span className="ml-2 font-medium">{entity.phone}</span>
                </div>
              )}
              {entity.birth_date && (
                <div>
                  <span className="text-gray-600">Date de naissance:</span>
                  <span className="ml-2 font-medium">{new Date(entity.birth_date).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
              {entity.birth_place && (
                <div>
                  <span className="text-gray-600">Lieu de naissance:</span>
                  <span className="ml-2 font-medium">{entity.birth_place}</span>
                </div>
              )}
              {entity.gender && (
                <div>
                  <span className="text-gray-600">Genre:</span>
                  <span className="ml-2 font-medium">
                    {entity.gender === 'male' ? 'Homme' : entity.gender === 'female' ? 'Femme' : 'Autre'}
                  </span>
                </div>
              )}
              {entity.country && (
                <div>
                  <span className="text-gray-600">Pays:</span>
                  <span className="ml-2 font-medium">{entity.country}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Adresse Section */}
      {(entity.address_line1 || entity.city || entity.postal_code) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Adresse</span>
            </h3>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
            >
              <Edit className="h-4 w-4" />
              <span>Modifier</span>
            </button>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            {entity.address_line1 && (
              <p className="text-sm text-gray-700">{entity.address_line1}</p>
            )}
            {entity.address_line2 && (
              <p className="text-sm text-gray-700">{entity.address_line2}</p>
            )}
            <div className="flex items-center space-x-2 mt-2">
              {entity.postal_code && (
                <span className="text-sm text-gray-700">{entity.postal_code}</span>
              )}
              {entity.city && (
                <span className="text-sm text-gray-700">{entity.city}</span>
              )}
              {entity.postal_code && entity.city && entity.country && (
                <span className="text-sm text-gray-500">,</span>
              )}
              {entity.country && (
                <span className="text-sm text-gray-700">{entity.country}</span>
              )}
            </div>
            {entity.state_province && (
              <p className="text-sm text-gray-600 mt-1">{entity.state_province}</p>
            )}
          </div>
        </div>
      )}

      {/* Documents Section */}
      <EntityDocuments />

      {/* Modal d'édition */}
      <AddEntityModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        entityType={isCompany ? 'entreprise' : 'personne'}
        onSubmit={handleEditSubmit}
        editingEntity={entity ? {
          id: entity.id,
          name: entity.name,
          kind: entity.kind,
          email: entity.email
        } : null}
        isEditMode={true}
        organizationId={orgId}
      />
    </div>
  );
};

export default EntityIdentity;

